import React, { useEffect, useRef, useState, type JSX } from "react";

import { Divider } from "@impero-com/ui";

import type { NewUserTag } from "api/generated/model";
import { Guid } from "utils/guid";

import * as theme from "../editableBaseTagListPlus/theme.css";

import { CascaderOption } from "./editableTagListPlus";
import { TagAddInput } from "./tagAddInput";
import { BaseTag } from "./types";

type SetAllTags = (tag: NewUserTag) => Promise<Guid>;
interface DropdownWithFooterProps<Tag> {
    menus: React.ReactElement;
    canCreateTag: boolean;
    options: CascaderOption[];
    onCreateTag: (name: string, categoryId: string) => void;
    getTagCategoryId: (tag: Tag) => Guid;
    isTagActive: (tag: Tag) => boolean;
    allTags: Tag[];
    setSelectedTag: (value: string) => void;
}
const DropdownWithFooter = <Tag extends BaseTag>({
    menus,
    canCreateTag,
    options,
    onCreateTag,
    getTagCategoryId,
    isTagActive,
    allTags,
}: DropdownWithFooterProps<Tag>): JSX.Element => {
    const [focusedOption, setFocusedOption] = useState<
        CascaderOption | undefined
    >();
    const menusRef = useRef<HTMLDivElement>(null);

    // Updates focusedOption by listening to active class name change
    useEffect(() => {
        if (menusRef.current === null) {
            return;
        }
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === "attributes" ||
                    mutation.attributeName === "class"
                ) {
                    const target = mutation.target;
                    if (!(target instanceof HTMLElement)) {
                        throw new Error(
                            "Expected target to be instance of HTMLElement"
                        );
                    }
                    // Expecting to find "ant-cascader-menu-item-active", which can possibly change in a future antd update
                    if (target.className.includes("active")) {
                        if (target.parentElement === null) {
                            throw new Error("Expected to find parent element");
                        }
                        const siblings = Array.from(
                            target.parentElement.childNodes
                        );
                        // Rendered list items must be in the same order as options array
                        const index = [...siblings].findIndex(
                            (a) => a === target
                        );
                        setFocusedOption(options[index]);
                    }
                }
            });
        });
        const targetNode = menusRef.current.getElementsByTagName("ul");
        const listItems = targetNode[0].childNodes;
        if (listItems) {
            listItems.forEach((item) => {
                observer.observe(item, {
                    attributes: true,
                    attributeFilter: ["class"],
                });
            });
        }
        return () => {
            observer.disconnect();
        };
    }, [
        options,
        setFocusedOption,
        // We need menus in the dependency array to trigger the useEffect when menus change.
        // In order for MutationObserver to work properly, we need to ensure that each listItems
        // are still in DOM, which is not the case after a search.
        menus,
    ]);

    if (!canCreateTag) {
        return menus;
    }
    return (
        <div className={theme.cascaderDropdown}>
            <div ref={menusRef}>{menus}</div>
            {focusedOption && (
                <>
                    <Divider className={theme.menuDivider} />
                    <TagAddInput
                        categoryOption={focusedOption}
                        onCreateTag={onCreateTag}
                        getTagCategoryId={getTagCategoryId}
                        isTagActive={isTagActive}
                        allTags={allTags}
                    />
                </>
            )}
        </div>
    );
};

export type { SetAllTags, DropdownWithFooterProps };
export { DropdownWithFooter };
